import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Card, Upload, Button, Progress, Alert, Table, Tag, Typography, Space, Collapse, message,
} from "antd";
import { InboxOutlined, CloudUploadOutlined, FileZipOutlined } from "@ant-design/icons";
import { useCookies } from "react-cookie";
import axios from "../../utils/axios";
import rawAxios from "axios"; // plain client, no baseURL/auth interceptors — used for the direct-to-R2 PUT

const { Dragger } = Upload;
const { Text, Paragraph } = Typography;

const API = process.env.REACT_APP_API_URL;
const POLL_MS = 2000;

const STATUS_TAG = {
    created: <Tag color="green">Tạo mới</Tag>,
    updated: <Tag color="blue">Cập nhật</Tag>,
    skipped: <Tag color="orange">Bỏ qua</Tag>,
    error: <Tag color="red">Lỗi</Tag>,
    ok: <Tag>OK</Tag>,
};

export default function ZipImporter({ compact = false }) {
    const [cookies] = useCookies(["admin"]);
    const adminId = Array.isArray(cookies?.admin) ? cookies.admin[0]?.id : cookies?.admin?.id;

    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [job, setJob] = useState(null);
    const pollRef = useRef(null);

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    useEffect(() => stopPolling, []);

    const pollJob = useCallback((jobId) => {
        stopPolling();
        pollRef.current = setInterval(async () => {
            try {
                const res = await axios.get(`${API}/admin/import/jobs/${jobId}`);
                setJob(res.data);
                if (res.data.status === "done" || res.data.status === "error") {
                    stopPolling();
                    setUploading(false);
                    if (res.data.status === "done") message.success("Import xong!");
                    else message.error("Import lỗi: " + (res.data.error || ""));
                }
            } catch (e) {
                stopPolling();
                setUploading(false);
                message.error("Mất kết nối tới job import.");
            }
        }, POLL_MS);
    }, []);

    // Upload goes straight from the browser to Cloudflare R2 (presigned URL) instead of
    // through our own server — a 600MB+ zip would otherwise get rejected by the host's
    // own web-server request-size limit before it even reaches Node.
    const startImport = async () => {
        const rawFile = fileList[0];
        const file = rawFile?.originFileObj || rawFile;
        if (!file) return message.warning("Chọn 1 file .zip trước đã.");

        setUploading(true);
        setJob({ status: "running", phase: "Đang xin quyền tải lên Cloudflare R2…", progress: 0, log: [], summary: {}, items: [] });
        try {
            const presign = await axios.post(`${API}/admin/import/presign`, { filename: file.name });
            const { key, url } = presign.data;

            setJob((j) => ({ ...j, phase: "Đang tải file lên Cloudflare R2… 0%" }));
            await rawAxios.put(url, file, {
                timeout: 0,
                transformRequest: [(data) => data], // send the raw file, don't let axios touch it
                onUploadProgress: (evt) => {
                    const pct = evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0;
                    setJob((j) => ({ ...j, progress: Math.min(pct, 99), phase: `Đang tải file lên Cloudflare R2… ${pct}%` }));
                },
            });

            setJob((j) => ({ ...j, phase: "Đã tải lên R2 — đang bắt đầu xử lý…", progress: 99 }));
            const start = await axios.post(`${API}/admin/import/start`, { key, create_by: adminId });
            pollJob(start.data.jobId);
        } catch (e) {
            setUploading(false);
            setJob(null);
            message.error(e?.message || "Tải file thất bại.");
        }
    };

    const reset = () => {
        stopPolling();
        setJob(null);
        setFileList([]);
        setUploading(false);
    };

    const draggerProps = {
        multiple: false,
        maxCount: 1,
        accept: ".zip",
        fileList,
        beforeUpload: (file) => {
            const isZip = file.name.toLowerCase().endsWith(".zip");
            if (!isZip) {
                message.error("Chỉ nhận file .zip");
                return Upload.LIST_IGNORE;
            }
            setFileList([file]);
            return false; // don't auto-upload
        },
        onRemove: () => setFileList([]),
    };

    const pct = job?.progress || 0;
    const running = job && (job.status === "running" || job.status === "queued");

    const itemColumns = [
        { title: "Topic", dataIndex: "topic", key: "topic", ellipsis: true },
        { title: "Course", dataIndex: "course", key: "course", ellipsis: true },
        { title: "Lesson", dataIndex: "lesson", key: "lesson", ellipsis: true },
        {
            title: "Phần",
            dataIndex: "parts",
            key: "parts",
            render: (parts, r) => (
                <Space size={[0, 4]} wrap>
                    {(parts || []).map((p) => <Tag key={p}>{p}</Tag>)}
                    {r.image ? <Tag color="cyan">ảnh</Tag> : null}
                </Space>
            ),
        },
        { title: "KQ", dataIndex: "status", key: "status", width: 90, render: (s) => STATUS_TAG[s] || s },
        { title: "Ghi chú", dataIndex: "note", key: "note", ellipsis: true, render: (n) => n ? <Text type="warning">{n}</Text> : "—" },
    ];

    return (
        <Card
            className="rounded-xl shadow-sm border-0"
            title={<span className="font-semibold"><FileZipOutlined /> Nhập nội dung từ file ZIP</span>}
            headStyle={{ borderBottom: "2px solid #f0f0f0" }}
        >
            {!compact && (
                <Alert
                    type="info"
                    showIcon
                    className="mb-4"
                    message="Cấu trúc file ZIP"
                    description={
                        <div className="text-xs leading-6">
                            <Paragraph className="!mb-1">
                                Nén theo 2 cấp thư mục <b>Topic / Course</b>. File của mọi lesson nằm <b>phẳng
                                ngay trong thư mục Course</b> — hệ thống tự gộp file thành lesson theo tên:
                            </Paragraph>
                            <pre className="bg-gray-50 p-2 rounded border overflow-auto !mb-1">{`Effortless English/
  Original Effortless English/
    Thriving On Chaos.mp3            (không từ khoá = Main Story)
    Thriving On Chaos.wav.srt
    Thriving On Chaos Vocab.mp3      ("Vocab" ở cuối = Vocabulary)
    Thriving On Chaos MiniStory.mp3  ("MiniStory" = Mini Story)
    Validation-Audio.mp3             (lesson khác: "Validation")
    Validation-Vocab.mp3`}</pre>
                            <ul className="list-disc pl-5 !mb-0">
                                <li>Từ khoá ở <b>cuối tên file</b> xác định phần: <code>Vocab</code>, <code>MiniStory</code>/<code>MS</code>, <code>POV</code>, <code>Comment</code>. Không có từ khoá → Main Story.</li>
                                <li>File cùng tên lesson (sau khi bỏ từ khoá) gộp chung 1 bài — dấu cách/<code>+</code>/<code>-</code>/<code>_</code> coi như nhau.</li>
                                <li><code>.srt</code> (hoặc <code>.wav.srt</code>) → lời (LRC) &nbsp;•&nbsp; <code>.vi.srt</code>/<code>_vi.srt</code> → bản dịch &nbsp;•&nbsp; <code>.mp3</code> → audio, <code>.mp4</code> → video</li>
                                <li>Thiếu phần nào / thiếu ảnh cũng được, phần đó để trống.</li>
                                <li>Topic/Course/Lesson trùng slug sẽ được <b>cập nhật</b>, chưa có thì tạo mới. Vẫn hỗ trợ kiểu cũ mỗi lesson 1 thư mục riêng.</li>
                            </ul>
                        </div>
                    }
                />
            )}

            {!job && (
                <>
                    <Dragger {...draggerProps} disabled={uploading}>
                        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                        <p className="ant-upload-text">Kéo thả file .zip vào đây, hoặc bấm để chọn</p>
                        <p className="ant-upload-hint">Chỉ 1 file .zip mỗi lần. File được tải thẳng lên Cloudflare R2 nên vài trăm MB–vài GB vẫn nhập được, chỉ mất thêm thời gian tải lên.</p>
                    </Dragger>
                    <Button
                        type="primary"
                        icon={<CloudUploadOutlined />}
                        className="mt-4 bg-blue-500"
                        disabled={!fileList.length}
                        loading={uploading}
                        onClick={startImport}
                    >
                        Bắt đầu nhập
                    </Button>
                </>
            )}

            {job && (
                <div>
                    <Progress
                        percent={pct}
                        status={job.status === "error" ? "exception" : job.status === "done" ? "success" : "active"}
                    />
                    <Text type="secondary">{job.phase}{job.totalLessons ? ` — ${job.processedLessons}/${job.totalLessons} lesson` : ""}</Text>

                    {job.status === "error" && (
                        <Alert type="error" showIcon className="mt-3" message="Import lỗi" description={job.error} />
                    )}

                    {job.summary && Object.keys(job.summary).length > 0 && (
                        <Space wrap className="mt-3">
                            <Tag color="green">Lesson tạo: {job.summary.lessonsCreated || 0}</Tag>
                            <Tag color="blue">Lesson cập nhật: {job.summary.lessonsUpdated || 0}</Tag>
                            <Tag color="geekblue">Topic: +{job.summary.topicsCreated || 0} / ~{job.summary.topicsUpdated || 0}</Tag>
                            <Tag color="geekblue">Course: +{job.summary.coursesCreated || 0} / ~{job.summary.coursesUpdated || 0}</Tag>
                            <Tag color="purple">File đã upload: {job.summary.filesUploaded || 0}</Tag>
                            {job.summary.errors ? <Tag color="red">Lỗi/bỏ qua: {job.summary.errors}</Tag> : null}
                        </Space>
                    )}

                    {job.items && job.items.length > 0 && (
                        <Table
                            className="mt-3"
                            size="small"
                            rowKey={(r, i) => i}
                            dataSource={job.items}
                            columns={itemColumns}
                            pagination={job.items.length > 10 ? { pageSize: 10 } : false}
                            scroll={{ x: 700 }}
                        />
                    )}

                    <Collapse
                        className="mt-3"
                        size="small"
                        items={[{
                            key: "log",
                            label: `Nhật ký (${job.log?.length || 0} dòng)`,
                            children: (
                                <pre className="text-xs max-h-72 overflow-auto bg-gray-50 p-2 rounded">
                                    {(job.log || []).map((l) =>
                                        `${new Date(l.t).toLocaleTimeString("vi-VN")}  ${l.level === "error" ? "❌" : l.level === "warn" ? "⚠️" : "•"} ${l.message}`
                                    ).join("\n")}
                                </pre>
                            ),
                        }]}
                    />

                    {!running && (
                        <Button className="mt-3" onClick={reset}>Nhập file khác</Button>
                    )}
                </div>
            )}
        </Card>
    );
}
